import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { BarChart3, RefreshCw, CheckCircle2, CircleDashed, ChevronDown, ChevronUp } from "lucide-react";
import { API, authHeaders } from "./shared";

const EVENT_LABELS = {
  page_view: "Page views",
  view_item_list: "Service / shop list views",
  view_item: "Service selected (view)",
  select_item: "Shop product clicks",
  add_to_cart: "Booking time chosen (add to cart)",
  begin_checkout: "Deposit checkout started",
  purchase: "Completed bookings ($25 deposit paid)",
  booking_click: "Booking button clicks",
  booking_submitted: "Booking forms submitted",
  view_promotion: "Wednesday promo banner views",
  select_promotion: "Wednesday promo clicks",
};

const fmtAgo = (iso) => {
  if (!iso) return "never";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
  return `${Math.floor(mins / 1440)}d ago`;
};

const Ga4StatusPanel = () => {
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showGuide, setShowGuide] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/admin/ga4-status`, authHeaders())
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    axios.get(`${API}/admin/meta-capi-status`, authHeaders())
      .then((r) => setMeta(r.data))
      .catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return null;

  return (
    <div data-testid="ga4-status-panel" className="mt-10 border border-brand-border bg-brand-surface/50 p-5">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-serif text-lg text-brand-text flex items-center gap-2">
          <BarChart3 size={16} className="text-brand-gold" /> GA4 Tracking Status
        </h3>
        <div className="flex items-center gap-3">
          <span data-testid="ga4-live-badge" className={`font-sans text-[10px] uppercase tracking-wider px-2 py-1 border ${data.tracking_live ? "border-green-500/40 text-green-400 bg-green-500/10" : "border-amber-500/40 text-amber-400 bg-amber-500/10"}`}>
            {data.tracking_live ? "Events Firing" : "No Events Yet"}
          </span>
          <button onClick={load} disabled={loading} data-testid="ga4-refresh-btn"
            className="flex items-center gap-1.5 font-sans text-[11px] px-3 py-1.5 border border-brand-border text-brand-text-muted hover:border-brand-gold hover:text-brand-gold transition-colors disabled:opacity-50">
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>
      <p className="font-sans text-xs text-brand-text-muted mb-4">
        GTM container <span className="text-brand-gold">{data.gtm_container}</span> is on every page.
        {" "}{data.total_7d} events tracked in the last 7 days · last event {fmtAgo(data.last_event_at)}.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5 mb-4" data-testid="ga4-events-list">
        {data.events.map((e) => (
          <div key={e.event} data-testid={`ga4-event-${e.event}`} className="flex items-center gap-2 py-1 border-b border-brand-border/30">
            {e.count_7d > 0
              ? <CheckCircle2 size={13} className="text-green-400 shrink-0" />
              : <CircleDashed size={13} className="text-brand-text-muted shrink-0" />}
            <span className="font-sans text-xs text-brand-text flex-1">{EVENT_LABELS[e.event] || e.event}</span>
            <span className={`font-sans text-xs font-semibold ${e.count_7d > 0 ? "text-brand-gold" : "text-brand-text-muted"}`}>{e.count_7d}</span>
            <span className="font-sans text-[10px] text-brand-text-muted w-16 text-right">{e.last_at ? fmtAgo(e.last_at) : "—"}</span>
          </div>
        ))}
      </div>

      {meta && (
        <div data-testid="meta-capi-status" className="mb-4 flex flex-wrap items-center gap-2 border border-brand-border bg-brand-bg/50 px-3 py-2">
          <span className="font-sans text-xs text-brand-text font-medium">Meta Conversions API:</span>
          <span data-testid="meta-capi-badge" className={`font-sans text-[10px] uppercase tracking-wider px-2 py-0.5 border ${meta.configured ? "border-green-500/40 text-green-400 bg-green-500/10" : "border-brand-border text-brand-text-muted"}`}>
            {meta.configured ? "Connected" : "Not Configured"}
          </span>
          {meta.configured && (
            <span className="font-sans text-[11px] text-brand-text-muted">
              Pixel {meta.pixel_id} · {meta.sent_7d} server events sent
              {meta.errors_7d > 0 && <span className="text-amber-400"> · {meta.errors_7d} errors</span>} (7d)
            </span>
          )}
        </div>
      )}

      <button onClick={() => setShowGuide(!showGuide)} data-testid="ga4-guide-toggle"
        className="flex items-center gap-1.5 font-sans text-xs text-brand-gold hover:underline">
        {showGuide ? <ChevronUp size={13} /> : <ChevronDown size={13} />} How to finish the GA4 connection (5 minutes)
      </button>
      {showGuide && (
        <ol data-testid="ga4-guide" className="mt-3 space-y-2 font-sans text-xs text-brand-text-muted list-decimal list-inside border border-brand-border p-4 bg-brand-bg/50">
          <li>Go to <span className="text-brand-text">analytics.google.com</span> → Admin → <span className="text-brand-text">Create Property</span> (name it "Afro Magic Braiding Hair"). Add a <span className="text-brand-text">Web data stream</span> with your site URL and copy the <span className="text-brand-gold">Measurement ID</span> (looks like G-XXXXXXXXXX).</li>
          <li>Go to <span className="text-brand-text">tagmanager.google.com</span> and open container <span className="text-brand-gold">{data.gtm_container}</span>.</li>
          <li>Click <span className="text-brand-text">Tags → New → Google Analytics → Google Tag</span>. Paste your Measurement ID. Set the trigger to <span className="text-brand-text">All Pages</span>. Name it "GA4 – Config" and save.</li>
          <li>Add a second tag: <span className="text-brand-text">Tags → New → Google Analytics: GA4 Event</span>. Event Name: <span className="text-brand-gold">{"{{Event}}"}</span>. Trigger: <span className="text-brand-text">Custom Event</span>, event name matching RegEx: <span className="text-brand-gold">view_item_list|view_item|select_item|add_to_cart|begin_checkout|purchase|booking_click|booking_submitted|view_promotion|select_promotion</span>. Name it "GA4 – Site Events" and save.</li>
          <li>Click <span className="text-brand-text">Submit → Publish</span> in the top-right. Done — within 30 minutes events appear in GA4 under Reports → Realtime.</li>
        </ol>
      )}
    </div>
  );
};

export default Ga4StatusPanel;
